import dayjs from 'dayjs';
import { isFunction } from 'lodash';
import LocalStorage from '/@/modules/core/app/helpers/LocalStorage';
import Logger from '/@/modules/core/app/helpers/Logger';

export class CacheHelper {
  storageEngine: typeof LocalStorage;

  constructor(storageEngine: typeof LocalStorage = LocalStorage) {
    const missingFunctions: string[] = ['getItem', 'setItem', 'removeItem'].filter(
      (func: string) => !isFunction(storageEngine[func])
    );
    if (missingFunctions.length) {
      const message: string = `StorageEngine object missing required functions: [${missingFunctions.join(
        ', '
      )}]`;
      Logger.log(message, Logger.LEVEL_ERROR);
    }

    this.storageEngine = storageEngine;
  }

  /**
   * Sets an item into browser's storageEngine with given expiry
   */
  set(key: string, value: unknown, expireAt: number | null = null): boolean {
    const item: object = {
      value,
      expireAt
    };
    try {
      this.storageEngine.setItem(key, JSON.stringify(item));
    } catch (e) {
      // return false in case of error like storageEngine is full
      return false;
    }
    return true;
  }

  /**
   * Returns item if exist and not expired
   */
  get(key: string): unknown | undefined {
    const itemStr: unknown | undefined = this.storageEngine.getItem(key);
    if (!itemStr) {
      return undefined;
    }
    try {
      const item: unknown = JSON.parse(itemStr);
      if (this.isExpired(item)) {
        this.remove(key);
        return undefined;
      }
      return item.value;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Removes previously set item
   */
  remove(key: string): void {
    this.storageEngine.removeItem(key);
  }

  /**
   * Compare the expireAt time of the item with the current time
   */
  private isExpired(item: unknown): boolean {
    return item.expireAt && dayjs().isAfter(dayjs.unix(item.expireAt));
  }
}

export default new CacheHelper();
